import { getApprovalRequests, markApprovalRequestRead } from "lib/api/approvalRequests";
import { getReceivedOrganizationInvitations } from "lib/api/getReceivedOrganizationInvitations";
import {
  getOrganizationCalendarCommentNotifications,
  getOrganizationCalendarTaskNotifications,
  markOrganizationCalendarCommentNotificationRead,
  markOrganizationCalendarTaskNotificationRead,
} from "lib/api/organizationCalendar";
import { getManagerCreationRequests } from "lib/api/roleAdmin";

export const NOTIFICATION_SOURCES = {
  ORGANIZATION_INVITATION: "organization_invitation",
  SIGNUP_APPROVAL: "signup_approval",
  MANAGER_CREATION: "manager_creation_request",
  SCHEDULE_COMMENT: "schedule_comment",
  SCHEDULE_TASK_REQUEST: "schedule_task_request",
};

export const REMOTE_NOTIFICATION_SOURCES = Object.values(NOTIFICATION_SOURCES);
export const GLOBAL_NOTIFICATIONS_REFRESH_EVENT =
  "global-notifications:refresh";

const READ_STORAGE_PREFIX = "globalNotifications.read";
const MAX_STORED_READ_IDS = 500;

function getReadStorageKey(userId) {
  return `${READ_STORAGE_PREFIX}.${String(userId ?? "anonymous")}`;
}

export function getStoredReadNotificationIds(userId) {
  if (typeof window === "undefined" || !userId) return new Set();

  try {
    const savedIds = JSON.parse(
      window.localStorage.getItem(getReadStorageKey(userId)) || "[]",
    );
    return new Set(Array.isArray(savedIds) ? savedIds.map(String) : []);
  } catch {
    return new Set();
  }
}

export function persistReadNotificationIds(userId, notificationIds) {
  if (typeof window === "undefined" || !userId) return;

  try {
    const ids = Array.from(new Set(notificationIds.map(String))).slice(
      -MAX_STORED_READ_IDS,
    );
    window.localStorage.setItem(getReadStorageKey(userId), JSON.stringify(ids));
  } catch {
    // The in-memory read state remains available when storage is unavailable.
  }
}

function buildNotificationId(source, sourceId) {
  return `${source}:${String(sourceId ?? "")}`;
}

function getRequestPersonLabel(request) {
  const payload = request?.payload ?? {};
  return (
    payload.user_name ??
    payload.userName ??
    payload.email ??
    request?.requester_email ??
    request?.requesterEmail ??
    "사용자"
  );
}

function mapOrganizationInvitation(invitation, readIds) {
  const source = NOTIFICATION_SOURCES.ORGANIZATION_INVITATION;
  const id = buildNotificationId(source, invitation.id);
  const organizationName = invitation.organizationName || "조직";
  const inviter = invitation.invitedByName
    ? `${invitation.invitedByName}님이 보낸 초대입니다.`
    : "새로운 조직 초대가 도착했습니다.";

  return {
    id,
    source,
    sourceId: invitation.id,
    title: `${organizationName} 구성원 초대`,
    message: inviter,
    createdAt: invitation.createdAt,
    read: readIds.has(id),
    type: "info",
    path: "/organization",
  };
}

function mapSignupApprovalRequest(request, readIds) {
  const source = NOTIFICATION_SOURCES.SIGNUP_APPROVAL;
  const id = buildNotificationId(source, request.id);
  const person = getRequestPersonLabel(request);
  const company = request?.payload?.company_name ?? request?.payload?.companyName;

  return {
    id,
    source,
    sourceId: request.id,
    title: "계정 생성 요청",
    message: `${person}님의 계정 생성 요청입니다.${
      company ? ` · ${company}` : ""
    }`,
    createdAt: request.created_at ?? request.createdAt,
    read: Boolean(request.is_read ?? request.isRead) || readIds.has(id),
    type: "warning",
    path: "/admin/signup-requests",
  };
}

function mapManagerCreationRequest(request, readIds) {
  const source = NOTIFICATION_SOURCES.MANAGER_CREATION;
  const id = buildNotificationId(source, request.id);
  const person = getRequestPersonLabel(request);

  return {
    id,
    source,
    sourceId: request.id,
    title: "매니저 계정 생성 요청",
    message: `${person}님의 매니저 계정 생성 요청입니다.`,
    createdAt: request.created_at ?? request.createdAt,
    read: Boolean(request.is_read ?? request.isRead) || readIds.has(id),
    type: "warning",
    path: "/admin/signup-requests",
  };
}

function mapScheduleCommentNotification(notification, readIds) {
  const source = NOTIFICATION_SOURCES.SCHEDULE_COMMENT;
  const id = buildNotificationId(source, notification.id);
  const isReply = notification.notificationType === "comment_reply";
  const authorName = notification.authorName || "구성원";
  const scheduleTitle = notification.scheduleTitle || "일정";
  const content = String(notification.commentContent ?? "").trim();
  const params = new URLSearchParams({
    tab: "calendar",
    schedule_id: String(notification.scheduleId ?? ""),
  });

  if (notification.organizationId) {
    params.set("organization_id", notification.organizationId);
  }
  if (notification.scheduleStartAt) {
    params.set("schedule_date", notification.scheduleStartAt);
  }

  return {
    id,
    source,
    sourceId: notification.id,
    title: isReply
      ? "내 댓글에 답글이 등록되었습니다."
      : "내 일정에 댓글이 등록되었습니다.",
    message: `${authorName}님 · ${scheduleTitle}${
      content ? ` · ${content}` : ""
    }`,
    createdAt: notification.createdAt,
    read: Boolean(notification.isRead) || readIds.has(id),
    type: "info",
    path: `/organization?${params.toString()}`,
  };
}

function mapScheduleTaskNotification(notification, readIds) {
  const source = NOTIFICATION_SOURCES.SCHEDULE_TASK_REQUEST;
  const id = buildNotificationId(source, notification.id);
  const notificationType = notification.notificationType ?? "task_request";
  const isIncomingRequest = notificationType === "task_request";
  const isAccepted = notification.requestStatus === "accepted";
  const isRejected = notification.requestStatus === "rejected";
  const actionRequired =
    isIncomingRequest && notification.requestStatus === "pending";
  const requesterName = notification.requesterName || "구성원";
  const assigneeName = notification.assigneeName || "구성원";
  const scheduleTitle = notification.scheduleTitle || "업무 요청";
  const rejectionReason = String(notification.rejectionReason ?? "").trim();
  const params = new URLSearchParams({
    tab: "calendar",
    schedule_id: String(notification.scheduleId ?? ""),
  });

  if (notification.organizationId) {
    params.set("organization_id", notification.organizationId);
  }
  if (notification.scheduleStartAt) {
    params.set("schedule_date", notification.scheduleStartAt);
  }

  let title = "새 업무 요청이 도착했습니다.";
  let message = `${requesterName}님이 '${scheduleTitle}' 업무를 요청했습니다.`;
  let type = "warning";

  if (notificationType === "task_request_accepted") {
    title = "업무 요청이 수락되었습니다.";
    message = `${assigneeName}님이 '${scheduleTitle}' 업무를 수락했습니다.`;
    type = "success";
  } else if (notificationType === "task_request_rejected") {
    title = "업무 요청이 반려되었습니다.";
    message = `${assigneeName}님이 '${scheduleTitle}' 업무를 반려했습니다.${
      rejectionReason ? ` 사유: ${rejectionReason}` : ""
    }`;
    type = "error";
  } else if (isAccepted) {
    title = "수락한 업무 요청입니다.";
    message = `'${scheduleTitle}' 업무 요청을 수락했습니다.`;
    type = "success";
  } else if (isRejected) {
    title = "반려한 업무 요청입니다.";
    message = `'${scheduleTitle}' 업무 요청을 반려했습니다.${
      rejectionReason ? ` 사유: ${rejectionReason}` : ""
    }`;
    type = "error";
  }

  return {
    id,
    source,
    sourceId: notification.id,
    title,
    message,
    createdAt: notification.createdAt,
    read: Boolean(notification.isRead) || readIds.has(id),
    type,
    path: `/organization?${params.toString()}`,
    actionRequired,
    scheduleId: notification.scheduleId,
    requestStatus: notification.requestStatus,
    rejectionReason,
  };
}

export async function loadGlobalNotifications({ userId, role } = {}) {
  const readIds = getStoredReadNotificationIds(userId);
  const canReviewSignupRequests = ["admin", "manager", "master"].includes(
    role,
  );
  const signupRequestPromise = canReviewSignupRequests
    ? getApprovalRequests({ requestType: "signup_approval", status: "pending" })
    : Promise.resolve(null);
  const managerRequestPromise =
    role === "master"
      ? getManagerCreationRequests({ status: "pending" })
      : Promise.resolve(null);
  const commentNotificationPromise =
    getOrganizationCalendarCommentNotifications();
  const taskNotificationPromise = getOrganizationCalendarTaskNotifications();
  const [
    invitationResult,
    signupResult,
    managerResult,
    commentNotificationResult,
    taskNotificationResult,
  ] = await Promise.all([
    getReceivedOrganizationInvitations(),
    signupRequestPromise,
    managerRequestPromise,
    commentNotificationPromise,
    taskNotificationPromise,
  ]);
  const notifications = [];
  const synchronizedSources = [];

  if (invitationResult?.success) {
    synchronizedSources.push(NOTIFICATION_SOURCES.ORGANIZATION_INVITATION);
    const invitations = Array.isArray(invitationResult.data?.invitations)
      ? invitationResult.data.invitations
      : [];
    notifications.push(
      ...invitations.map((invitation) =>
        mapOrganizationInvitation(invitation, readIds),
      ),
    );
  }

  if (signupResult?.success) {
    synchronizedSources.push(NOTIFICATION_SOURCES.SIGNUP_APPROVAL);
    const requests = Array.isArray(signupResult.data?.requests)
      ? signupResult.data.requests
      : [];
    notifications.push(
      ...requests.map((request) => mapSignupApprovalRequest(request, readIds)),
    );
  }

  if (managerResult?.success) {
    synchronizedSources.push(NOTIFICATION_SOURCES.MANAGER_CREATION);
    const requests = Array.isArray(managerResult.data?.requests)
      ? managerResult.data.requests
      : [];
    notifications.push(
      ...requests.map((request) =>
        mapManagerCreationRequest(request, readIds),
      ),
    );
  }

  if (commentNotificationResult?.success) {
    synchronizedSources.push(NOTIFICATION_SOURCES.SCHEDULE_COMMENT);
    const commentNotifications = Array.isArray(
      commentNotificationResult.data?.notifications,
    )
      ? commentNotificationResult.data.notifications
      : [];
    notifications.push(
      ...commentNotifications.map((notification) =>
        mapScheduleCommentNotification(notification, readIds),
      ),
    );
  }

  if (taskNotificationResult?.success) {
    synchronizedSources.push(NOTIFICATION_SOURCES.SCHEDULE_TASK_REQUEST);
    const taskNotifications = Array.isArray(
      taskNotificationResult.data?.notifications,
    )
      ? taskNotificationResult.data.notifications
      : [];
    notifications.push(
      ...taskNotifications.map((notification) =>
        mapScheduleTaskNotification(notification, readIds),
      ),
    );
  }

  return { notifications, synchronizedSources };
}

export function markNotificationReadOnServer(notification) {
  if (
    notification?.source === NOTIFICATION_SOURCES.SIGNUP_APPROVAL &&
    notification?.sourceId
  ) {
    return markApprovalRequestRead(notification.sourceId);
  }

  if (
    notification?.source === NOTIFICATION_SOURCES.SCHEDULE_COMMENT &&
    notification?.sourceId
  ) {
    return markOrganizationCalendarCommentNotificationRead(
      notification.sourceId,
    );
  }

  if (
    notification?.source === NOTIFICATION_SOURCES.SCHEDULE_TASK_REQUEST &&
    notification?.sourceId
  ) {
    return markOrganizationCalendarTaskNotificationRead(
      notification.sourceId,
    );
  }

  return Promise.resolve({ success: true });
}

export function requestGlobalNotificationsRefresh() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(GLOBAL_NOTIFICATIONS_REFRESH_EVENT));
}
